import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { HelpFAQ as HelpFAQType } from '../../types';

interface HelpFAQProps {
  faqs: HelpFAQType[];
  searchQuery?: string;
}

interface FAQItemProps {
  faq: HelpFAQType;
  isExpanded: boolean;
  onToggle: () => void;
  searchQuery?: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ faq, isExpanded, onToggle, searchQuery }) => {
  // 検索クエリがある場合はハイライト表示
  const highlightText = (text: string, query?: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className='bg-yellow-200 dark:bg-yellow-800 px-1 rounded'>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className='border border-gray-200 dark:border-gray-700 rounded-lg mb-4'>
      <button
        onClick={onToggle}
        className='w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700'
        aria-expanded={isExpanded}
      >
        <div className='flex items-center justify-between'>
          <h3 className='text-lg font-medium text-gray-800 dark:text-white pr-4'>
            {highlightText(faq.question, searchQuery)}
          </h3>
          {isExpanded ? (
            <ChevronUpIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
          ) : (
            <ChevronDownIcon className='h-5 w-5 text-gray-500 flex-shrink-0' />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className='px-4 pb-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'>
          <div className='pt-4 prose dark:prose-invert max-w-none'>
            <div
              className='text-gray-700 dark:text-gray-300'
              dangerouslySetInnerHTML={{
                __html: faq.answer.replace(/\n/g, '<br />'),
              }}
            />
          </div>
          <div className='mt-4 flex flex-wrap gap-2'>
            {faq.tags.map(tag => (
              <span
                key={tag}
                className='inline-block px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full'
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const HelpFAQ: React.FC<HelpFAQProps> = ({ faqs, searchQuery }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (faqId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    setExpandedItems(new Set(faqs.map(faq => faq.id)));
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  if (faqs.length === 0) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500 dark:text-gray-400'>
          {searchQuery
            ? `「${searchQuery}」に関するFAQが見つかりませんでした。`
            : 'FAQがありません。'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* 全開閉ボタン */}
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xl font-semibold text-gray-800 dark:text-white'>
          よくある質問
          {searchQuery && (
            <span className='text-sm font-normal text-gray-500 dark:text-gray-400 ml-2'>
              「{searchQuery}」の検索結果: {faqs.length}件
            </span>
          )}
        </h2>
        <div className='flex space-x-2'>
          <button
            onClick={expandAll}
            className='text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200'
          >
            すべて開く
          </button>
          <span className='text-gray-300 dark:text-gray-600'>|</span>
          <button
            onClick={collapseAll}
            className='text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200'
          >
            すべて閉じる
          </button>
        </div>
      </div>

      {/* FAQ一覧 */}
      <div>
        {faqs.map(faq => (
          <FAQItem
            key={faq.id}
            faq={faq}
            isExpanded={expandedItems.has(faq.id)}
            onToggle={() => toggleExpanded(faq.id)}
            searchQuery={searchQuery}
          />
        ))}
      </div>
    </div>
  );
};

export default HelpFAQ;
